// DEPLOY: /var/www/woody.wtf/frontend/src/pages/TradingTrade.js
// Trading > Trade (/trading/trade). Ticker search (from our instruments, /api/chart/search),
// live quote with change and spread (refreshes every 5s), price chart (1D / 5D / 1M / 6M bars
// from IBKR, plain SVG with a crosshair readout), Level 2 depth on demand, and the order ticket:
// market, limit, stop, stop-limit, trailing stop and bracket (entry + take-profit + stop-loss),
// Day or GTC, US pre/post-market; sized by shares, dollar amount or % of available funds;
// Preview runs IBKR's what-if (commission, margin) without placing. Open orders for the
// symbol sit underneath with Modify / Cancel. URL ?code=US.AAPL&side=SELL&qty=10 prefills
// (used by Close on the Portfolio page and the Options page). Options (code OP.<conId>) trade here
// too: sized in contracts, cost x the contract multiplier, links go to the underlying.
// Admin-only for now (useTradingAccount guard; API is @admin_required in trading.py).
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import TradingTabs from '../components/TradingTabs';
import OpenOrders from '../components/OpenOrders';
import api from '../api';
import {
  s, REFRESH_MS, isNum, num, signed, tone, arrow, qty, price, chartTicker, errText, isOption,
  useTradingAccount, StatusBar, useAccountData, SymbolSearch,
} from '../components/tradingShared';

const RANGES = ['1D', '5D', '1M', '6M'];
const TYPES = [
  ['LIMIT', 'Limit', 'Buys or sells at your price or better.'],
  ['MARKET', 'Market', 'Fills straight away at the best available price.'],
  ['STOP', 'Stop', 'Becomes a market order once the stop price trades.'],
  ['STOP_LIMIT', 'Stop limit', 'Becomes a limit order once the stop price trades.'],
  ['TRAIL', 'Trailing stop', 'A stop that follows the price by a fixed amount or percent.'],
  ['BRACKET', 'Bracket', 'Limit entry with a take-profit and a stop-loss attached; one exit cancels the other.'],
];
const SIZE_MODES = [['shares', 'Shares'], ['amount', 'Amount'], ['percent', '% of funds']];
const ccyOf = code => code.startsWith('AU.') ? 'AUD' : 'USD';   // stocks; an option takes its quote's currency

// ── Quote header ─────────────────────────────────────────────────────────────
function QuotePanel({ code, quote, err }) {
  const last = quote?.last_price, prev = quote?.prev_close_price;
  const chg = isNum(last) && isNum(prev) ? last - prev : null;
  const pct = isNum(chg) && prev ? chg / prev * 100 : null;
  const spread = isNum(quote?.bid_price) && isNum(quote?.ask_price) ? quote.ask_price - quote.bid_price : null;
  const live = quote?.market_data_type === 'live';
  const stat = (label, v) => (
    <div><div style={s.label}>{label}</div><div style={{ ...s.mono, fontSize: '13px' }}>{v}</div></div>
  );
  return (
    <div style={s.card}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '20px', fontWeight: 600, ...s.mono }}>{code}</span>
        {quote?.desc && quote.desc !== code && <span style={{ ...s.meta, ...s.mono }}>{quote.desc}</span>}
        {quote && <span style={live ? s.pill('var(--success-dim)', 'var(--success)') : s.pill('var(--bg)', 'var(--text-muted)')}>
          {live ? 'LIVE' : (quote.market_data_type || 'no data').toUpperCase()}</span>}
        {(() => { const u = quote?.underlying_code || (isOption(code) ? null : code); return u && <>
          <a style={{ ...s.link, marginLeft: 'auto' }} href={`/chart?t=${encodeURIComponent(chartTicker(u))}`} target="_blank" rel="noopener noreferrer">Full chart{isOption(code) ? ` (${u})` : ''} ↗</a>
          <a style={s.link} href={`/company?ticker=${encodeURIComponent(chartTicker(u))}`} target="_blank" rel="noopener noreferrer">Company ↗</a>
          <Link style={s.link} to={`/trading/options?code=${encodeURIComponent(u)}`}>Options chain</Link>
        </>; })()}
      </div>
      {err && <div style={s.err}>✕ {err}</div>}
      {!quote && !err && <div style={{ ...s.meta, marginTop: '8px' }}>Loading quote…</div>}
      {quote && (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', margin: '8px 0 14px' }}>
            <span style={{ fontSize: '30px', fontWeight: 600, ...s.mono, letterSpacing: '-0.02em' }}>{price(last)}</span>
            <span style={{ ...s.mono, fontSize: '15px', color: tone(chg) }}>{arrow(chg)}{signed(chg, Math.abs(chg) < 1 ? 3 : 2)} ({isNum(pct) ? `${signed(pct)}%` : '—'})</span>
            <span style={s.meta}>{quote.currency}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '12px' }}>
            {stat('Bid', `${price(quote.bid_price)} × ${qty(quote.bid_vol)}`)}
            {stat('Ask', `${price(quote.ask_price)} × ${qty(quote.ask_vol)}`)}
            {stat('Spread', isNum(spread) ? price(spread) : '—')}
            {stat('Open', price(quote.open_price))}
            {stat('High', price(quote.high_price))}
            {stat('Low', price(quote.low_price))}
            {stat('Prev close', price(prev))}
            {stat('Volume', qty(quote.volume))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Price chart: close line, prev-close reference, crosshair readout ──────────
function PriceChart({ broker, code, prevClose }) {
  const [rng, setRng]     = useState('1D');
  const [bars, setBars]   = useState(null);
  const [err, setErr]     = useState('');
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  useEffect(() => {
    setBars(null); setErr(''); setHover(null);
    api.get(`/trading/${broker}/bars`, { params: { code, range: rng } })
      .then(r => setBars(r.data.bars)).catch(e => setErr(errText(e)));
  }, [broker, code, rng]);

  const W = 700, H = 220, L = 8, R = 60, T = 12, B = 22;
  const cs = (bars || []).map(b => b.c);
  const ref = rng === '1D' && isNum(prevClose) ? prevClose : null;
  let lo = Math.min(...cs, ...(ref !== null ? [ref] : [])), hi = Math.max(...cs, ...(ref !== null ? [ref] : []));
  const pad = (hi - lo) * 0.08 || hi * 0.01 || 1; lo -= pad; hi += pad;
  const x = i => L + i * (W - L - R) / Math.max(cs.length - 1, 1);
  const y = v => T + (hi - v) / (hi - lo) * (H - T - B);
  const up = cs.length > 1 && cs[cs.length - 1] >= (ref ?? cs[0]);
  const stroke = up ? 'var(--success)' : 'var(--error)';
  const fmtT = t => { const d = new Date(t); return ['1D', '5D'].includes(rng)
    ? d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }); };

  const move = e => {
    const r = svgRef.current.getBoundingClientRect();
    const vx = (e.clientX - r.left) / r.width * W;
    setHover(Math.min(cs.length - 1, Math.max(0, Math.round((vx - L) / (W - L - R) * (cs.length - 1)))));
  };
  const hb = hover !== null && bars ? bars[hover] : null;

  return (
    <div style={s.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
        <span style={s.title}>Price</span>
        <span style={{ ...s.meta, marginLeft: '8px', ...s.mono }}>
          {hb ? `${fmtT(hb.t)}  O ${price(hb.o)}  H ${price(hb.h)}  L ${price(hb.l)}  C ${price(hb.c)}  V ${qty(hb.v)}` : ''}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
          {RANGES.map(r => <button key={r} style={{ ...s.btn, padding: '4px 10px', ...(r === rng ? { background: 'var(--accent)', color: 'var(--on-accent)', border: 'none' } : {}) }}
                                   onClick={() => setRng(r)}>{r}</button>)}
        </div>
      </div>
      {err && <div style={s.err}>✕ {err}</div>}
      {!bars && !err && <div style={{ ...s.meta, height: `${H}px` }}>Loading chart…</div>}
      {bars && !cs.length && <div style={{ ...s.meta, padding: '30px 0' }}>No price bars returned - IBKR needs a market data subscription for this market.</div>}
      {cs.length > 0 && (
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block', cursor: 'crosshair' }}
             onMouseMove={move} onMouseLeave={() => setHover(null)}>
          {[hi - pad, (hi + lo) / 2, lo + pad].map((v, i) => (
            <g key={i}>
              <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} style={{ stroke: 'var(--border)' }} strokeWidth="1" />
              <text x={W - R + 6} y={y(v) + 4} fontSize="11" style={{ fill: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{price(v)}</text>
            </g>
          ))}
          {ref !== null && <>
            <line x1={L} x2={W - R} y1={y(ref)} y2={y(ref)} style={{ stroke: 'var(--text-muted)' }} strokeWidth="1" strokeDasharray="4 4" />
            <text x={L + 4} y={y(ref) - 4} fontSize="10" style={{ fill: 'var(--text-muted)' }}>prev close</text>
          </>}
          <path d={cs.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('')} fill="none" style={{ stroke }} strokeWidth="2" strokeLinejoin="round" />
          <text x={L} y={H - 6} fontSize="10" style={{ fill: 'var(--text-muted)' }}>{fmtT(bars[0].t)}</text>
          <text x={W - R} y={H - 6} fontSize="10" textAnchor="end" style={{ fill: 'var(--text-muted)' }}>{fmtT(bars[bars.length - 1].t)}</text>
          {hover !== null && <>
            <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} style={{ stroke: 'var(--text-muted)' }} strokeWidth="1" />
            <circle cx={x(hover)} cy={y(cs[hover])} r="4" style={{ fill: stroke, stroke: 'var(--surface)' }} strokeWidth="2" />
          </>}
          <rect x={L} y={T} width={W - L - R} height={H - T - B} fill="transparent" />
        </svg>
      )}
    </div>
  );
}

// ── Level 2 depth, on demand (IBKR allows only a few depth lines at once) ─────
function Depth({ broker, code }) {
  const [d, setD]       = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');
  useEffect(() => { setD(null); setErr(''); }, [code]);
  const load = async () => {
    setBusy(true); setErr('');
    try { setD((await api.get(`/trading/${broker}/depth`, { params: { code } })).data); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };
  const maxSize = Math.max(1, ...[...(d?.bids || []), ...(d?.asks || [])].map(x => Number(x.size) || 0));
  const side = (rows, bid) => (
    <table style={s.table}>
      <thead><tr><th style={s.th}>{bid ? 'Bid' : 'Ask'}</th><th style={{ ...s.th, textAlign: 'right' }}>Size</th><th style={s.th}>Venue</th></tr></thead>
      <tbody>{rows.map((x, i) => (
        <tr key={i} style={{ background: `linear-gradient(to ${bid ? 'left' : 'right'}, ${bid ? 'var(--success-dim)' : 'var(--error-dim)'} ${Number(x.size) / maxSize * 100}%, transparent 0)` }}>
          <td style={{ ...s.td, color: bid ? 'var(--success)' : 'var(--error)' }}>{price(x.price)}</td>
          <td style={{ ...s.td, textAlign: 'right' }}>{qty(x.size)}</td>
          <td style={{ ...s.td, color: 'var(--text-muted)' }}>{x.venue || ''}</td>
        </tr>
      ))}</tbody>
    </table>
  );
  return (
    <div style={s.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={s.title}>Market depth</span>
        <span style={s.meta}>Level 2 snapshot - needs a depth subscription for this market</span>
        <button style={{ ...s.btn, marginLeft: 'auto' }} onClick={load} disabled={busy}>{busy ? 'Loading…' : d ? 'Refresh' : 'Load depth'}</button>
      </div>
      {err && <div style={s.err}>✕ {err}</div>}
      {d && (d.bids.length || d.asks.length ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
          {side(d.bids, true)}{side(d.asks, false)}
        </div>
      ) : <div style={{ ...s.meta, marginTop: '10px' }}>No depth returned - check your market depth subscriptions in the IBKR portal.</div>)}
    </div>
  );
}

// ── Order ticket ─────────────────────────────────────────────────────────────
function Ticket({ broker, acc, code, quote, funds, fx, init, onPlaced }) {
  const blank = { side: 'BUY', order_type: 'LIMIT', size_mode: 'shares', size: '', price: '', aux_price: '',
                  trail_type: 'amount', trail_value: '', take_profit: '', stop_loss: '', tif: 'DAY', extended: false };
  const [o, setO] = useState({ ...blank, ...init });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg]   = useState('');
  const [err, setErr]   = useState('');
  const set = patch => { setO(x => ({ ...x, ...patch })); setPreview(null); };

  // New symbol: keep side / type / sizing mode, clear the prices.
  useEffect(() => { setO(x => ({ ...x, price: '', aux_price: '', take_profit: '', stop_loss: '' })); setPreview(null); setMsg(''); setErr(''); }, [code]);
  // First quote for a symbol fills an empty limit / entry price with the last price
  // (only a quote for this code - the old one lingers for a moment after switching).
  const last = quote?.code === code ? quote.last_price : null;
  useEffect(() => {
    if (isNum(last)) setO(x => x.price ? x : { ...x, price: String(last) });
  }, [code, last]);

  const opt  = isOption(code);
  const ccy  = (quote?.code === code && quote.currency) || ccyOf(code);
  const mult = (opt && quote?.code === code && Number(quote.multiplier)) || 1;   // option: x100 (or the contract's multiplier)
  const unit = opt ? 'contracts' : 'shares';
  const needs = { LIMIT: ['price'], STOP: ['aux_price'], STOP_LIMIT: ['aux_price', 'price'], TRAIL: ['trail_value'], BRACKET: ['price', 'take_profit', 'stop_loss'], MARKET: [] }[o.order_type];
  const refPrice = Number(o.order_type === 'STOP' ? o.aux_price : needs.includes('price') ? o.price : last) || 0;
  const avail = isNum(funds?.AvailableFunds) && (ccy === 'AUD' || fx?.[ccy]) ? Number(funds.AvailableFunds) / (fx?.[ccy] || 1) : null;
  const shares = o.size_mode === 'shares' ? Number(o.size) || 0
    : !refPrice ? 0
    : o.size_mode === 'amount' ? Math.floor((Number(o.size) || 0) / (refPrice * mult))
    : avail ? Math.floor(avail * (Number(o.size) || 0) / 100 / (refPrice * mult)) : 0;
  const cost = shares * refPrice * mult;
  const extOk = !opt && ['LIMIT', 'STOP_LIMIT'].includes(o.order_type);
  const ready = code && shares > 0 && needs.every(f => Number(o[f]) > 0) && !busy;

  // Bracket risk / reward per share and in total.
  const risk = o.order_type === 'BRACKET' && Number(o.price) && Number(o.stop_loss) ? Math.abs(o.price - o.stop_loss) : null;
  const reward = o.order_type === 'BRACKET' && Number(o.price) && Number(o.take_profit) ? Math.abs(o.take_profit - o.price) : null;

  const body = (q = shares) => ({ acc_id: acc.acc_id, code, side: o.side, order_type: o.order_type, qty: q, tif: o.tif,
                        extended: extOk && o.extended, price: o.price, aux_price: o.aux_price, trail_type: o.trail_type,
                        trail_value: o.trail_value, take_profit: o.take_profit, stop_loss: o.stop_loss });
  const summary = (q = shares) => {
    const t = TYPES.find(x => x[0] === o.order_type)[1];
    const px = { LIMIT: `limit ${o.price}`, STOP: `stop ${o.aux_price}`, STOP_LIMIT: `stop ${o.aux_price}, limit ${o.price}`,
                 TRAIL: `trail ${o.trail_value}${o.trail_type === 'percent' ? '%' : ''}`, MARKET: 'at market',
                 BRACKET: `entry ${o.price}, take-profit ${o.take_profit}, stop-loss ${o.stop_loss}` }[o.order_type];
    return `${o.side} ${qty(q)} ${opt ? `${unit} of ${quote?.desc || code}` : code} - ${t}, ${px}, ${o.tif}${extOk && o.extended ? ', pre/post-market' : ''}\n≈ ${num(q * refPrice * mult)} ${ccy}`;
  };

  const doPreview = async () => {
    setBusy('preview'); setErr(''); setMsg('');
    try { setPreview((await api.post(`/trading/${broker}/whatif`, body())).data.preview); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(''); }
  };
  // Buying by amount / % of funds: shrink the size so IBKR's commission fits inside the budget.
  const fitQty = async () => {
    if (o.side !== 'BUY' || o.size_mode === 'shares') return shares;
    const budget = o.size_mode === 'amount' ? Number(o.size) : avail * Number(o.size) / 100;
    const w = (await api.post(`/trading/${broker}/whatif`, body())).data.preview;
    const fee = [w.commission, w.maxCommission].find(v => isNum(v) && v < 1e300) || 0;   // IBKR sends 1.79e308 for unset
    return Math.min(shares, Math.floor((budget - fee) / (refPrice * mult)));
  };
  // IBKR 201 "Available converted to base: X ... Cash needed ...: Y" -> shortfall in the order currency.
  const cashShort = e => {
    const m = /Available converted to base: ([\d.]+).*Cash needed[^:]*: ([\d.]+)/.exec(errText(e) || '');
    return m && (m[2] - m[1]) / (fx?.[ccy] || 1);
  };
  const place = async () => {
    setBusy('place'); setErr(''); setMsg('');
    try {
      let q = await fitQty();
      if (q < 1) throw new Error('Amount is too small to cover the price plus commission');
      if (acc.trd_env === 'REAL' && !window.confirm(`LIVE ORDER with real money on account ${acc.acc_id}:\n\n${summary(q)}\n\nPlace it?`)) return;
      let r;
      try { r = await api.post(`/trading/${broker}/order`, body(q)); }
      catch (e) {
        const short = o.side === 'BUY' && o.size_mode !== 'shares' && cashShort(e);
        if (!short) throw e;
        q -= Math.ceil(short / (refPrice * mult));   // drop just enough shares to cover IBKR's stated shortfall, retry once
        if (q < 1) throw e;
        r = await api.post(`/trading/${broker}/order`, body(q));
      }
      setMsg(r.data.message);
      setPreview(null); onPlaced();
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(''); }
  };

  const field = (f, label, hint) => (
    <div key={f}>
      <div style={s.label}>{label}</div>
      <input style={s.input} type="number" min="0" step="any" value={o[f]} onChange={e => set({ [f]: e.target.value })} placeholder={hint} />
    </div>
  );
  const buy = o.side === 'BUY';
  const pv = preview;

  return (
    <div style={{ ...s.card, position: 'sticky', top: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <span style={s.title}>Order ticket</span>
        <span style={s.badge(acc.trd_env === 'REAL')}>{acc.trd_env === 'REAL' ? 'LIVE - REAL MONEY' : 'PAPER'}</span>
      </div>

      <div style={{ display: 'flex', marginBottom: '14px' }}>
        {['BUY', 'SELL'].map((sd, i) => (
          <button key={sd} onClick={() => set({ side: sd })}
                  style={{ ...s.seg(o.side === sd), borderRadius: i ? '0 var(--radius) var(--radius) 0' : 'var(--radius) 0 0 var(--radius)',
                           ...(o.side === sd ? { background: sd === 'BUY' ? 'var(--success)' : 'var(--error)', borderColor: 'transparent' } : {}) }}>
            {sd === 'BUY' ? 'Buy' : 'Sell'}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: '12px' }}>
        <div style={s.label}>Order type</div>
        <select style={{ ...s.select, width: '100%' }} value={o.order_type} onChange={e => set({ order_type: e.target.value })}>
          {TYPES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <div style={{ ...s.meta, marginTop: '4px' }}>{TYPES.find(x => x[0] === o.order_type)[2]}</div>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', marginBottom: '6px' }}>
          {SIZE_MODES.map(([k, label], i) => (
            <button key={k} onClick={() => set({ size_mode: k, size: '' })}
                    style={{ ...s.seg(o.size_mode === k), fontSize: '11px', padding: '5px 0',
                             borderRadius: i === 0 ? 'var(--radius) 0 0 var(--radius)' : i === SIZE_MODES.length - 1 ? '0 var(--radius) var(--radius) 0' : 0 }}>{k === 'shares' && opt ? 'Contracts' : label}</button>
          ))}
        </div>
        <input style={s.input} type="number" min="0" step="any" value={o.size} onChange={e => set({ size: e.target.value })}
               placeholder={o.size_mode === 'shares' ? `Number of ${unit}` : o.size_mode === 'amount' ? `Amount in ${ccy}` : 'Percent of available funds'} />
        <div style={{ ...s.meta, marginTop: '4px' }}>
          {o.size_mode !== 'shares' && `= ${qty(shares)} ${unit} · `}≈ {num(cost)} {ccy}{opt && ` (x${mult} per contract)`}
          {o.size_mode === 'percent' && (avail !== null ? ` · ${num(avail)} ${ccy} available` : ` · no ${ccy} exchange rate from IBKR yet`)}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
        {o.order_type === 'LIMIT' && field('price', 'Limit price')}
        {o.order_type === 'STOP' && field('aux_price', 'Stop price')}
        {o.order_type === 'STOP_LIMIT' && <>{field('aux_price', 'Stop price')}{field('price', 'Limit price')}</>}
        {o.order_type === 'TRAIL' && <>
          <div>
            <div style={s.label}>Trail by</div>
            <select style={{ ...s.select, width: '100%' }} value={o.trail_type} onChange={e => set({ trail_type: e.target.value })}>
              <option value="amount">Amount ({ccy})</option><option value="percent">Percent</option>
            </select>
          </div>
          {field('trail_value', o.trail_type === 'percent' ? 'Trail %' : 'Trail amount')}
        </>}
        {o.order_type === 'BRACKET' && <>
          {field('price', 'Entry (limit)')}
          <div />
          {field('take_profit', 'Take-profit')}
          {field('stop_loss', 'Stop-loss')}
        </>}
      </div>
      {isNum(risk) && isNum(reward) && risk > 0 && (
        <div style={{ ...s.meta, marginBottom: '12px' }}>
          Risk {num(risk * shares * mult)} {ccy} · reward {num(reward * shares * mult)} {ccy} · <b style={{ color: 'var(--text)' }}>{(reward / risk).toFixed(2)} : 1</b>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ flex: 1 }}>
          <div style={s.label}>Time in force</div>
          <select style={{ ...s.select, width: '100%' }} value={o.tif} onChange={e => set({ tif: e.target.value })}>
            <option value="DAY">Day</option><option value="GTC">Good till cancelled</option>
          </select>
        </div>
        <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px', marginTop: '18px', cursor: extOk ? 'pointer' : 'default',
                        color: extOk ? 'var(--text)' : 'var(--text-muted)' }} title="US pre-market and after-hours. Limit and stop-limit only.">
          <input type="checkbox" checked={extOk && o.extended} disabled={!extOk} onChange={e => set({ extended: e.target.checked })} />
          Pre/post-market
        </label>
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button style={{ ...s.btn, flex: 1 }} onClick={doPreview} disabled={!ready}>{busy === 'preview' ? 'Checking…' : 'Preview'}</button>
        <button onClick={place} disabled={!ready}
                style={{ ...s.btn, flex: 2, border: 'none', fontSize: '13px', fontWeight: 600,
                         background: ready ? (buy ? 'var(--success)' : 'var(--error)') : 'var(--border)',
                         color: ready ? 'var(--on-accent)' : 'var(--text-muted)', cursor: ready ? 'pointer' : 'default' }}>
          {busy === 'place' ? 'Placing…' : `${buy ? 'Buy' : 'Sell'} ${shares ? qty(shares) : ''} ${opt ? unit : code.split('.')[1] || ''}`}
        </button>
      </div>
      {msg && <div style={s.ok}>{msg}</div>}
      {err && <div style={s.err}>✕ {err}</div>}

      {pv && (
        <div style={{ marginTop: '14px', padding: '12px', background: 'var(--bg)', borderRadius: 'var(--radius)', fontSize: '12px' }}>
          <div style={{ ...s.title, fontSize: '12px', marginBottom: '8px' }}>Preview{o.order_type === 'BRACKET' ? ' (entry order)' : ''} - nothing placed</div>
          {[
            ['Estimated cost', `${num(cost)} ${ccy}`],
            ['Commission', isNum(pv.commission) ? `${num(pv.commission)} ${pv.commissionCurrency || ''}`
              : isNum(pv.minCommission) ? `${num(pv.minCommission)} – ${num(pv.maxCommission)} ${pv.commissionCurrency || ''}` : '—'],
            ['Initial margin change', num(pv.initMarginChange)],
            ['Maintenance margin change', num(pv.maintMarginChange)],
            ['Equity with loan after', num(pv.equityWithLoanAfter)],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
              <span style={{ color: 'var(--text-muted)' }}>{k}</span><span style={s.mono}>{v}</span>
            </div>
          ))}
          {pv.warningText && <div style={{ ...s.err, fontSize: '12px' }}>⚠ {pv.warningText}</div>}
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function TradingTrade() {
  const t = useTradingAccount();
  const [params] = useSearchParams();
  const [code, setCode]   = useState((params.get('code') || '').toUpperCase());
  const [quote, setQuote] = useState(null);
  const [qErr, setQErr]   = useState('');
  const { data, reload } = useAccountData(t.broker, t.acc, 'AUD');
  const init = { ...(params.get('side') && { side: params.get('side').toUpperCase() }),
                 ...(params.get('qty') && { size: params.get('qty'), size_mode: 'shares' }) };

  useEffect(() => { document.title = code ? `${code} · Trade | chart.ninja` : 'Trade | chart.ninja'; }, [code]);

  const loadQuote = useCallback(async () => {
    if (!t.broker || !code) return;
    try { setQuote((await api.get(`/trading/${t.broker}/quote`, { params: { code } })).data.quote); setQErr(''); }
    catch (e) { setQErr(errText(e)); }
  }, [t.broker, code]);
  useEffect(() => {
    setQuote(null); setQErr('');
    loadQuote();
    const i = setInterval(loadQuote, REFRESH_MS);
    return () => clearInterval(i);
  }, [loadQuote]);

  const orders = (data?.orders || []).filter(r => r.code === code);
  const held = (data?.positions || []).find(p => p.code === code);

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.content}>
        <h1 style={s.heading}>Trading</h1>
        <TradingTabs />
        <StatusBar t={t} />
        {t.acc && (
          <>
            <div style={{ marginBottom: '20px' }}><SymbolSearch onPick={setCode} /></div>
            {!code && <div style={{ ...s.card, ...s.meta, textAlign: 'center', padding: '40px' }}>Search for a company above to see its quote, chart and order ticket.</div>}
            {code && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: '20px', alignItems: 'start' }}>
                  <div>
                    <QuotePanel code={code} quote={quote} err={qErr} />
                    {held && (
                      <div style={{ ...s.card, display: 'flex', gap: '24px', flexWrap: 'wrap', fontSize: '13px' }}>
                        <span style={s.title}>Your position</span>
                        <span style={s.mono}>{qty(held.position)} @ {price(held.avg_cost / (held.multiplier || 1))}</span>
                        <span style={s.mono}>Value {num(held.market_value)} {held.currency}</span>
                        <span style={{ ...s.mono, color: tone(held.unrealized_pnl) }}>Unrealised {signed(held.unrealized_pnl)}</span>
                        <span style={{ ...s.mono, color: tone(held.daily_pnl) }}>Today {signed(held.daily_pnl)}</span>
                      </div>
                    )}
                    <PriceChart broker={t.broker} code={code} prevClose={quote?.prev_close_price} />
                    <Depth broker={t.broker} code={code} />
                  </div>
                  <Ticket broker={t.broker} acc={t.acc} code={code} quote={quote} funds={data?.funds?.[0]} fx={data?.fx}
                          init={init} onPlaced={reload} />
                </div>
                <div style={s.card}>
                  <div style={{ ...s.title, marginBottom: '6px' }}>Open orders for {code}</div>
                  <OpenOrders broker={t.broker} acc={t.acc} orders={orders} onChange={reload} empty={`No open orders for ${code}.`} />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
